import { memo, RefObject, useCallback, useMemo, useState } from 'react';
import {
  ColorValue,
  InputAccessoryView,
  NativeSyntheticEvent,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  TextInputChangeEventData,
  TextInputProps,
  TouchableOpacity,
} from 'react-native';
import { commonStyles } from '../common/styles';
import AppIcon, { AppIcons } from './app_icon';
import { useBoolStateCb } from '../common/hooks';
import AppColors from '../common/colors';
import { VoidCb } from '../types/others';
import localization from '../common/localization';

type Props = {
  id: string;
  value?: string;
  icon?: AppIcons;
  placeholder?: string;
  secured?: boolean;
  submitType?: TextInputProps['returnKeyType'];
  backgroundColor?: ColorValue;
  onSubmit?: VoidCb;
  onChanged?: (value: string) => void;
  inputRef?: RefObject<TextInput | null>;
};

const regex = /^\d*$/;

const LoginInput = memo<Props>(
  ({
    id,
    value,
    icon,
    placeholder,
    secured,
    submitType,
    backgroundColor,
    onSubmit,
    onChanged,
    inputRef: ref,
  }) => {
    const [focused, setFocused] = useState(false);

    const onChange = useCallback(
      (e: NativeSyntheticEvent<TextInputChangeEventData>) => {
        const value = e.nativeEvent.text;
        if (regex.test(value)) onChanged?.(value);
        else e.stopPropagation();
      },
      [onChanged],
    );

    const [focus, blur] = useBoolStateCb(setFocused);

    const focusSelf = useCallback(() => ref?.current?.focus(), []);

    const style = useMemo(
      () =>
        StyleSheet.compose(styles.container, {
          backgroundColor: backgroundColor ?? '#090A0A',
          borderColor: focused ? AppColors.mainBlue : '#303437',
        }),
      [focused, backgroundColor],
    );

    return (
      <Pressable onPress={focusSelf} style={style}>
        {icon !== undefined && (
          <AppIcon name={icon} color={'white'} scaled={false} />
        )}
        <TextInput
          value={value}
          onChange={onChange}
          onSubmitEditing={onSubmit}
          placeholder={placeholder}
          placeholderTextColor={'#979C9E'}
          onFocus={focus}
          onBlur={blur}
          style={styles.input}
          secureTextEntry={secured}
          keyboardAppearance='dark'
          keyboardType='decimal-pad'
          returnKeyType={submitType}
          inputAccessoryViewID={id}
          ref={ref}
        />
        {Platform.OS === 'ios' && (
          <InputAccessoryView nativeID={id}>
            <TouchableOpacity onPress={onSubmit} style={styles.accessoryButton}>
              <Text style={styles.accessoryText}>
                {submitType === 'next'
                  ? localization.general.next
                  : localization.account.signIn}
              </Text>
            </TouchableOpacity>
          </InputAccessoryView>
        )}
      </Pressable>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    borderWidth: 1,
    borderRadius: 8,
  },
  input: {
    ...commonStyles.text,
    flex: 1,
    alignSelf: 'stretch',
    marginLeft: 8,
    paddingVertical: 16,
    fontSize: 16,
    fontWeight: '400',
    color: 'white',
  },
  accessoryButton: {
    paddingVertical: 15,
    paddingHorizontal: 15,
    alignSelf: 'flex-end',
  },
  accessoryText: {
    fontSize: 18,
    color: AppColors.mainBlue,
  },
});

export default LoginInput;
